import {
  AccountInfo,
  AuthenticationResult,
  Configuration,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type RedirectRequest,
  type SilentRequest,
} from '@azure/msal-browser';
import { environment } from '@/config/environment';

type TokenClaims = Record<string, unknown>;

export interface MsalSessionData {
  accessToken: string;
  expiresOn: string;
  userId: string;
  claims: Array<{ typ: string; val: string }>;
}

let msalClient: PublicClientApplication | null = null;
let initializePromise: Promise<PublicClientApplication | null> | null = null;

const isBrowser = typeof window !== 'undefined';

const buildRedirectUri = () => {
  if (!isBrowser) {
    return undefined;
  }

  return `${window.location.origin}${window.location.pathname}${window.location.search}`;
};

const redirectUri = buildRedirectUri();

const msalConfig: Configuration | null = environment.aad.enabled
  ? {
    auth: {
      clientId: environment.aad.clientId,
      authority: environment.aad.authority,
      redirectUri,
      postLogoutRedirectUri: redirectUri,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
  }
  : null;

const loginScopes = environment.aad.enabled
  ? [environment.aad.apiScope, 'openid', 'profile', 'offline_access']
  : [];

const flattenClaimValue = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap(flattenClaimValue);
  }

  if (value == null) {
    return [];
  }

  if (typeof value === 'string') {
    return [value];
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return [String(value)];
  }

  return [];
};

const normalizeClaims = (claims: TokenClaims | undefined): Array<{ typ: string; val: string }> => {
  if (!claims) {
    return [];
  }

  return Object.entries(claims).flatMap(([typ, rawValue]) => flattenClaimValue(rawValue).map((val) => ({ typ, val })));
};

const decodeJwtClaims = (token: string): TokenClaims => {
  const parts = token.split('.');
  if (parts.length < 2) {
    return {};
  }

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = `${base64}${'='.repeat((4 - (base64.length % 4)) % 4)}`;
    const json = atob(padded);
    return JSON.parse(json) as TokenClaims;
  } catch {
    return {};
  }
};

const mergeClaims = (result: AuthenticationResult): Array<{ typ: string; val: string }> => {
  const idTokenClaims = normalizeClaims((result.idTokenClaims ?? {}) as TokenClaims);
  const accessTokenClaims = normalizeClaims(decodeJwtClaims(result.accessToken));
  return [...idTokenClaims, ...accessTokenClaims];
};

const pickUserId = (result: AuthenticationResult, claims: Array<{ typ: string; val: string }>): string => {
  const candidateTypes = new Set([
    'preferred_username',
    'upn',
    'email',
    'unique_name',
    'name',
  ]);

  const claimUserId = claims.find((claim) => candidateTypes.has(claim.typ.toLowerCase()))?.val;
  return claimUserId || result.account?.username || result.account?.name || result.account?.homeAccountId || '';
};

const ensureActiveAccount = (client: PublicClientApplication) => {
  const activeAccount = client.getActiveAccount();
  if (activeAccount) {
    return activeAccount;
  }

  const [firstAccount] = client.getAllAccounts();
  if (firstAccount) {
    client.setActiveAccount(firstAccount);
  }

  return firstAccount ?? null;
};

export const isMsalEnabled = () => environment.aad.enabled;

export const initializeMsal = async (): Promise<PublicClientApplication | null> => {
  if (!msalConfig || !isBrowser) {
    return null;
  }

  if (initializePromise) {
    return initializePromise;
  }

  initializePromise = (async () => {
    msalClient ??= new PublicClientApplication(msalConfig);
    await msalClient.initialize();
    const redirectResult = await msalClient.handleRedirectPromise();
    if (redirectResult?.account) {
      msalClient.setActiveAccount(redirectResult.account);
    } else {
      ensureActiveAccount(msalClient);
    }
    return msalClient;
  })().catch((error) => {
    initializePromise = null;
    throw error;
  });

  return initializePromise;
};

export const getMsalAccount = async (): Promise<AccountInfo | null> => {
  const client = await initializeMsal();
  if (!client) {
    return null;
  }

  return ensureActiveAccount(client);
};

export const startMsalLogin = async (): Promise<never> => {
  const client = await initializeMsal();
  if (!client) {
    throw new Error('MSAL is not enabled.');
  }

  const request: RedirectRequest = {
    scopes: loginScopes,
    redirectStartPage: window.location.href,
  };

  await client.loginRedirect(request);

  throw new Error('MSAL redirect started.');
};

export const acquireMsalToken = async (forceRefresh = false): Promise<MsalSessionData | null> => {
  const client = await initializeMsal();
  if (!client) {
    return null;
  }

  const account = ensureActiveAccount(client);
  if (!account) {
    return null;
  }

  const request: SilentRequest = {
    account,
    scopes: loginScopes,
    forceRefresh,
  };

  const result: AuthenticationResult = await client.acquireTokenSilent(request);

  client.setActiveAccount(result.account);
  const claims = mergeClaims(result);

  return {
    accessToken: result.accessToken,
    expiresOn: result.expiresOn?.toISOString() ?? new Date(Date.now() + 60 * 60 * 1000).toISOString(),
    userId: pickUserId(result, claims),
    claims,
  };
};

export const logoutMsal = async (): Promise<void> => {
  const client = await initializeMsal();
  if (!client) {
    return;
  }

  await client.logoutRedirect({
    account: client.getActiveAccount() ?? undefined,
    postLogoutRedirectUri: redirectUri,
  });
};

export { InteractionRequiredAuthError };
